import React, { useEffect, useRef, useState } from "react";
import { Avatar, Box, Button, Collapse, IconButton, Menu, MenuItem, TextField, Typography } from "@mui/material";
import { useTheme } from "@mui/styles";
import { useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import sh from "bundles/common/utils/sh";
import SquarehubLoader from "bundles/common/components/SquarehubLoader";

const formatDayLabel = (dateString, locale) => {
  if (!dateString) return "";

  return new Date(dateString).toLocaleDateString(locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const formatTime = (dateString, locale) => {
  if (!dateString) return "";

  return new Date(dateString).toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Perspective-agnostic conversation thread shared by recruiters and candidates.
// `selfType` ("Recruiter" | "Candidate") decides which messages render as "mine";
// `peerOf(detail)` returns the header { name, avatar, subtitle } for the other party.
const ConversationView = ({
  conversationId,
  detail,
  loading,
  onMessageSent,
  onBack,
  contextOpen,
  onToggleContext,
  contextPanel,
  showContextToggle = false,
  selfType,
  peerOf,
  messageEndpoint,
  onArchiveToggle,
  onBlockToggle,
}) => {
  const theme = useTheme();
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const i18nLocale = useSelector((state) => state.i18nLocale);

  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const threadRef = useRef(null);

  const messages = detail?.messages || [];
  const peer = detail ? peerOf(detail) : null;
  const blocked = !!detail?.blocked;
  const blockedByMe = !!detail?.blocked_by_me;
  const archived = !!detail?.archived;

  useEffect(() => {
    setBody("");
  }, [conversationId]);

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [messages.length, loading]);

  const sendMessage = () => {
    const trimmed = body.trim();
    if (!trimmed || sending || !conversationId || blocked) return;

    setSending(true);
    sh.post(messageEndpoint(conversationId), {
      authenticity_token: authenticityToken,
      body: trimmed,
    })
      .then((res) => {
        setBody("");
        if (res.data?.data) onMessageSent(res.data.data);
      })
      .catch((err) => {
        console.error(err);
        enqueueSnackbar(intl.formatMessage({ id: "messaging.error_sending" }), {
          variant: "error",
        });
      })
      .finally(() => setSending(false));
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const closeMenu = () => setMenuAnchor(null);

  const handleArchive = () => {
    closeMenu();
    onArchiveToggle?.();
  };

  const handleBlock = () => {
    closeMenu();
    onBlockToggle?.();
  };

  const renderMessages = () => {
    let lastDay = null;

    return messages.map((message) => {
      const day = message.created_at ? new Date(message.created_at).toDateString() : null;
      const showDaySeparator = day && day !== lastDay;
      lastDay = day;
      const fromSelf = message.sender_type === selfType;

      return (
        <React.Fragment key={message.id}>
          {showDaySeparator && (
            <Typography
              variant="caption"
              sx={{ textAlign: "center", my: 1.5, color: theme.palette.text.secondary }}
            >
              {formatDayLabel(message.created_at, i18nLocale)}
            </Typography>
          )}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: fromSelf ? "flex-end" : "flex-start",
              mb: 1.25,
            }}
          >
            <Box
              sx={{
                maxWidth: "72%",
                px: 2,
                py: 1.25,
                bgcolor: fromSelf ? theme.palette.primary.main : "hsl(160, 10%, 96%)",
                color: fromSelf ? theme.palette.common.white : theme.palette.text.main,
                borderRadius: fromSelf ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              <Typography variant="body2" sx={{ color: "inherit" }}>
                {message.body}
              </Typography>
            </Box>
            <Typography variant="body3" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
              {message.sender_name ? `${message.sender_name} · ` : ""}
              {formatTime(message.created_at, i18nLocale)}
            </Typography>
          </Box>
        </React.Fragment>
      );
    });
  };

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        bgcolor: theme.palette.common.white,
        border: "1px solid hsla(160, 10%, 90%, 0.6)",
        borderRadius: "16px",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.5,
          borderBottom: "1px solid hsl(160, 10%, 92%)",
        }}
      >
        {onBack && (
          <IconButton onClick={onBack} size="small" sx={{ color: theme.palette.text.main }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        )}
        <Avatar
          src={peer?.avatar || undefined}
          alt={peer?.name}
          sx={{ width: 40, height: 40 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" noWrap sx={{ color: theme.palette.text.main }}>
            {peer?.name}
          </Typography>
          {peer?.subtitle && (
            <Typography variant="body3" noWrap sx={{ color: theme.palette.text.light_grey, display: "block" }}>
              {peer.subtitle}
            </Typography>
          )}
        </Box>
        {showContextToggle && !contextOpen && (
          <Button
            onClick={onToggleContext}
            size="small"
            disableRipple
            sx={{
              flexShrink: 0,
              textTransform: "none",
              fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
              fontSize: "12px",
              borderRadius: "9999px",
              px: 1.5,
              color: "hsl(153, 68%, 25%)",
              border: "1px solid hsl(153, 68%, 25%)",
              "&:hover": { bgcolor: "hsla(153, 68%, 25%, 0.06)" },
            }}
          >
            <FormattedMessage id="recruiter.messaging.show_panel" />
          </Button>
        )}
        <IconButton
          size="small"
          onClick={(event) => setMenuAnchor(event.currentTarget)}
          sx={{ color: theme.palette.text.secondary, flexShrink: 0 }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu} disableScrollLock>
          <MenuItem onClick={handleArchive}>
            <FormattedMessage id={archived ? "messaging.unarchive" : "messaging.archive"} />
          </MenuItem>
          {(!blocked || blockedByMe) && (
            <MenuItem onClick={handleBlock}>
              <FormattedMessage id={blocked ? "messaging.unblock" : "messaging.block"} />
            </MenuItem>
          )}
        </Menu>
      </Box>

      {contextPanel && <Collapse in={contextOpen}>{contextPanel}</Collapse>}

      <Box ref={threadRef} sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2, display: "flex", flexDirection: "column" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
            <SquarehubLoader size={56} centered />
          </Box>
        ) : messages.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              <FormattedMessage id="messaging.no_messages" />
            </Typography>
          </Box>
        ) : (
          renderMessages()
        )}
      </Box>

      {blocked && (
        <Box
          sx={{
            px: 2,
            py: 1.25,
            bgcolor: "hsla(0, 70%, 50%, 0.06)",
            borderTop: "1px solid hsla(0, 70%, 50%, 0.2)",
          }}
        >
          <Typography variant="body2" sx={{ color: theme.palette.error.main, textAlign: "center" }}>
            <FormattedMessage id={blockedByMe ? "messaging.blocked_banner_self" : "messaging.blocked_banner_other"} />
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: 1.5,
          px: 2,
          py: 1.5,
          borderTop: "1px solid hsl(160, 10%, 92%)",
        }}
      >
        <TextField
          fullWidth
          multiline
          maxRows={4}
          size="small"
          value={body}
          onChange={(event) => setBody(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={intl.formatMessage({ id: "messaging.write_message" })}
          disabled={sending || !conversationId || blocked}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              "& fieldset": { borderColor: "hsl(160, 10%, 90%)" },
              "&.Mui-focused fieldset": { borderColor: "hsl(153, 68%, 25%)" },
              "&.Mui-focused": { boxShadow: "0 0 0 3px hsla(153, 68%, 25%, 0.15)" },
            },
          }}
        />
        <Button
          variant="rounded"
          color="primaryContained"
          size="small"
          onClick={sendMessage}
          disabled={sending || !body.trim() || blocked}
          sx={{
            px: 3,
            flexShrink: 0,
            textTransform: "none",
            fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
            "&.Mui-disabled": {
              bgcolor: "hsl(160, 10%, 90%)",
              color: theme.palette.text.secondary,
            },
          }}
        >
          <FormattedMessage id="messaging.send" />
        </Button>
      </Box>
    </Box>
  );
};

export default ConversationView;
